import { Alert, Box, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useEffect, useMemo, useState } from "react"
import { ResetTabledRepeat } from "event-definitions"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { If } from "lib/@components/switch"
import { NoResults } from "lib/@components/no-results/no-results"
import { ContextMenu } from "lib/@components/context-menu-bar"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { useDialog } from "lib/@hooks/useDialog"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { useParams } from "react-router"
import GenericSearch from "lib/@components/paging/generic-search"
import { AuthoringDeleteDialog } from "../../plugins/project-item/authoring-delete-dialog"
import {
    SimilarityCheckboxHeader,
    similarityTableHeaders,
    similarityTableHeadersWithoutSelection,
} from "./task-correlator/lib/similarity-table-headers"
import { SelectAllTasks } from "./task-correlator/components/select-all-tasks"
import { DeleteMenuItem } from "../../plugins/delete-menu-item"
import { AuthoringTaskList } from "./task-correlator/authoring-task-list"
import { TaskSimilarityItem } from "./task-correlator/task-similarity-item"
import { FindTasks } from "../../plugins/projectToolbar/find-tasks"
import { deleteSimilarTasksFromGroup } from "./controller/delete-similar-tasks-from-group"
import { SelectedItemsBanner } from "../../components/selected-items-banner"
import { GroupStatus } from "authoring/lib/enum/group-status.mjs"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import { useEnrichedMacroMap } from "authoring/routes/macro/hooks/use-enriched-macro-map"
import { collectMacroTokensFromSchedule } from "authoring/routes/macro/lib/macro-token-utils"
import { buildScheduleContent } from "authoring/routes/macro/lib/macro-conversion"
import { useDemand } from "lib/authorization/use-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { BaseComponentCard } from "./task-correlator/base-component-card"
import { AffectedSchedulesList } from "./task-correlator/affected-schedules-list"
import { uniqueId } from "library/database/split-id"
import { StandardSelector } from "lib/@components/combos/standard-selector"

export function OverwriteGroupTasks() {
    const [selectedTasks, setSelectedTasks] = useState({})
    const [search, setSearch] = useState("")
    const taskView = "tasks"
    const scheduleView = "schedules"
    const [view, setView] = useState(taskView)
    const { projectId, groupId } = useParams()
    const { project, projectGroup, groupLoading } = useBoundContext()
    const canEditSchedule = useDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)
    const { similarTasks, sourceTask, sourceTaskId } = projectGroup ?? {}
    const isGlobalUpdate = Boolean(sourceTask)
    // The group references a component version, but it no longer resolves.
    const componentMissing = !!sourceTaskId && !sourceTask
    // The saved version still resolves, but the component has since been deleted from the library.
    const componentRemoved = Boolean(sourceTask?.componentRemoved)
    useEffect(() => {
        if (isGlobalUpdate && projectGroup?.status === GroupStatus.APPLIED) setView("schedules")
    }, [isGlobalUpdate, projectGroup?.status])
    const tokenIds = useMemo(collectOverwriteTaskTokenIds, [sourceTask, similarTasks])
    useEffect(() => {
        ResetTabledRepeat.raiseOnce()
    }, [similarTasks])
    const { macroMap, loading: macrosLoading } = useEnrichedMacroMap({ tokenIds })
    const selectedTaskIds = Object.keys(selectedTasks)
    const selectedCount = selectedTaskIds?.length || 0
    const taskString = `${selectedCount === 1 ? taskView.singularize() : taskView}`
    const removesEveryTask = selectedCount >= (similarTasks?.length ?? 0)
    const disableDelete =
        projectGroup.status === GroupStatus.APPLIED || isLockedToLegacy(project?.phase) || removesEveryTask

    const deleteSimilarTaskDialog = useDialog(
        <AuthoringDeleteDialog
            value={true}
            title={`Delete ${taskString}`}
            message={
                selectedCount > 1
                    ? `Are you sure you would like to remove ${selectedCount} tasks from the group?`
                    : "Are you sure you would like to remove the selected task from the group?"
            }
        />
    )
    const hasTasks = similarTasks?.length > 0
    const displaySimilarTasks = useMemo(buildDisplaySimilarTasks, [similarTasks, macroMap])
    const affectedSchedules = useMemo(buildAffectedSchedules, [similarTasks])
    const findTasksButton = canEditSchedule ? <FindTasks /> : null
    const selectedTasksBanner = canEditSchedule ? (
        <SelectedItemsBanner
            selected={selectedTaskIds}
            label={"task"}
            handleDelete={handleDelete}
            disabled={disableDelete}
        />
    ) : null
    const tableSx = {
        "& tr td.MuiTableCell-root:last-of-type": {
            px: 0,
        },
    }

    SimilarityCheckboxHeader.usePlug(
        <If truthy={canEditSchedule && !projectGroup.readOnly}>
            <SelectAllTasks />
        </If>
    )
    ContextMenu("overwriteSimilarTask").plug(
        replaceAllWith(
            <DeleteMenuItem
                if={canDeleteSimilarTasks}
                handleDelete={handleDelete}
                disabled={projectGroup.readOnly || similarTasks?.length <= 1}
            />
        )
    )
    if (tokenIds.length > 0 && macrosLoading) return <LoadingScreen />
    if (groupLoading) return <LoadingScreen />

    return (
        <Bound
            sourceTask={sourceTask}
            selectedTasks={selectedTasks}
            setSelectedTasks={setSelectedTasks}
            hasTasks={hasTasks}
            allTasks={displaySimilarTasks}
        >
            <If truthy={componentRemoved}>
                <Alert severity="warning" data-cy="source-component-removed" sx={{ mb: 2 }}>
                    This component has been removed from the Component Library. This update uses historical version{" "}
                    {sourceTask?.componentVersion}.
                </Alert>
            </If>
            <If truthy={!!sourceTask}>
                <BaseComponentCard component={sourceTask} />
            </If>
            <If truthy={componentMissing}>
                <Alert severity="error" data-cy="source-component-missing" sx={{ my: 2 }}>
                    Selected component no longer exists
                </Alert>
            </If>
            <If truthy={isGlobalUpdate}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                    sx={{ mt: 2, mb: 1 }}
                >
                    <StandardSelector
                        value={view}
                        options={[
                            { value: taskView, label: taskView.titleize() },
                            { value: scheduleView, label: scheduleView.titleize() },
                        ]}
                        onChange={setView}
                        sortTitle="View:"
                        data-cy="global-update-view-select"
                    />
                    <If truthy={view === "tasks"}>
                        <GenericSearch
                            results={displaySimilarTasks}
                            searchPlaceHolder="Search"
                            search={search}
                            setSearch={setSearch}
                            showResultCount={false}
                            size="small"
                        />
                    </If>
                </Stack>
            </If>
            <If truthy={!isGlobalUpdate}>
                <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
                    <GenericSearch
                        results={displaySimilarTasks}
                        searchPlaceHolder="Search"
                        search={search}
                        setSearch={setSearch}
                        showResultCount={false}
                        size="small"
                    />
                </Stack>
            </If>

            <If truthy={!!hasTasks && view === "tasks"}>
                <Bound isSimilarResults>
                    <Stack my={1}>
                        <Typography variant="h6" ml={3}>
                            Similar Tasks
                        </Typography>
                        {selectedTasksBanner}
                        <AuthoringTaskList
                            tasks={displaySimilarTasks}
                            itemComponent={<TaskSimilarityItem selectable={!projectGroup.readOnly} />}
                            tableHeaders={
                                projectGroup.readOnly ? similarityTableHeadersWithoutSelection : similarityTableHeaders
                            }
                            sx={tableSx}
                        />
                    </Stack>
                </Bound>
            </If>
            <If truthy={!!affectedSchedules.length && view === "schedules"}>
                <AffectedSchedulesList scheduleIds={affectedSchedules.map(({ scheduleId }) => scheduleId)} />
            </If>
            <If truthy={!sourceTask && !hasTasks}>
                <Stack spacing={2} sx={{ alignItems: "center" }}>
                    <NoResults>{"You haven't added anything to this global update yet"}</NoResults>
                    <Box>{findTasksButton}</Box>
                </Stack>
            </If>
        </Bound>
    )

    function canDeleteSimilarTasks() {
        return canEditSchedule
    }

    async function handleDelete(taskIds) {
        try {
            const confirmDelete = await deleteSimilarTaskDialog()
            if (!confirmDelete) return

            const deleted = await deleteSimilarTasksFromGroup({
                projectId,
                groupId,
                taskIds,
            })
            if (!deleted) {
                errorSnackbar(`Failed to delete selected ${taskString}`)
                return
            }

            successSnackbar(`Successfully deleted selected ${taskString}`)
        } catch (e) {
            console.error(e)
            errorSnackbar(`Unexpected error while deleting ${taskString}`)
        }
    }

    function collectOverwriteTaskTokenIds() {
        const tokenSet = new Set()
        if (sourceTask) collectMacroTokensFromSchedule({ tasks: [sourceTask] }, tokenSet)
        for (const similarTask of similarTasks ?? []) {
            if (similarTask?.task) collectMacroTokensFromSchedule({ tasks: [similarTask.task] }, tokenSet)
        }
        return [...tokenSet]
    }

    function buildDisplaySimilarTasks() {
        return (similarTasks ?? []).map((similarTask) => ({
            ...similarTask,
            task: expandTask(similarTask?.task, macroMap),
        }))
    }

    function buildAffectedSchedules() {
        const schedulesById = new Map()
        for (const similarTask of similarTasks ?? []) {
            const scheduleId = uniqueId(similarTask?.scheduleId)
            if (!scheduleId || schedulesById.has(scheduleId)) continue
            schedulesById.set(scheduleId, {
                scheduleId,
                title: similarTask?.scheduleTitle,
                code: similarTask?.scheduleCode,
            })
        }
        return [...schedulesById.values()]
    }
}

function expandTask(task, macroMap) {
    if (!task) return task
    if (!macroMap || !Object.keys(macroMap).length) return task
    return buildScheduleContent({ tasks: [task] }, macroMap)?.tasks?.[0] ?? task
}
